import React, {
  memo,
  useRef,
  useState,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import tw, { styled } from 'twin.macro';
import { Link, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import chroma from 'chroma-js';

import { useQuery, useQueryClient } from '@sopranodesign/pulse-hooks';
import {
  motion,
  AnimatePresence,
  AnimateSharedLayout
} from '@sopranodesign/framer-motion';
import {
  Tag,
  Flex,
  Span,
  Label,
  Button,
  Divider,
  Heading1,
  Indicator,
  Paragraph,
  FilterMenu,
  PanelSlider,
  Placeholder,
  useFilterReducer,
  SecondarySidebarContainer
} from '@sopranodesign/pulse-components';
import { GroupSelect } from '@sopranodesign/pulse-groups';
import {
  useSubscription,
  useNotifySubscription
} from '@sopranodesign/pulse-subscribe';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

import progressing from '../../../sounds/progressing.mp3';
import submitted from '../../../sounds/submitted.mp3';
import {
  useBrowser,
  useDocument,
  useTicketMonitor,
  useUpdateTicketCache,
  useUpdateTicketResponsesCache
} from '../../../hooks/index';
import { LiveChatSettings } from '../../../Components/index';

const MessagePreview = styled(Paragraph)`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

const statuses = {
  in_progress: 'Claimed',
  submitted: 'Pending'
};

const Status = ({ children }) => (
  <div style={{ position: 'sticky', top: 0, background: 'white' }}>
    <Divider />
    <Flex middle style={{ marginTop: '-1.5rem' }}>
      <Label colour="grey" style={{ background: 'white', padding: '0 1rem' }} secondary>
        {children}
      </Label>
    </Flex>
  </div>
);

const HeadingContainer = styled(Flex)`
  ${tw`border-gray-300 border-solid border-0 border-b`}
`;

const SidebarLinkContainer = styled(motion.div)`
  &:hover {
    background: ${({ theme }) => chroma(theme.white).darken(0.25)};
  }
  ${props =>
    props.unread &&
    `background: ${chroma.mix(props.theme.primary, 'white', 0.75)};`}
  ${props =>
    props.active && `background: ${chroma(props.theme.white).darken(0.25)};`}
  & > a {
    color: inherit;
    text-decoration: inherit;
  }
`;

const REFRESH_INTERVAL = 8000;

const LiveChatSettingsWrapper = () => (
  <Flex col mt ySpace>
    <LiveChatSettings />
  </Flex>
);

const MemoizedLiveChatSettings = memo(LiveChatSettingsWrapper);

const Sidebar = ({ agent }) => {
  const progressingAudio = useRef(new Audio(progressing));
  const submittedAudio = useRef(new Audio(submitted));

  const location = useLocation();
  const { notify } = useBrowser();
  const { setTitle } = useDocument();
  const updateTicketCache = useUpdateTicketCache();
  const updateTicketResponsesCache = useUpdateTicketResponsesCache();
  const queryClient = useQueryClient();

  const [filteredGroupIds, setFilteredGroupIds] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [unreadTicketIds, setUnreadTicketIds] = useState([]);
  const [audioInitialized, setAudioInitialized] = useState(false);

  const agentGroupsQuery = useQuery({
    queryKey: [`tickets/ubidesk/agents/me/groups`]
  });

  useEffect(() => {
    if (agentGroupsQuery.isSuccess) {
      const ids = agentGroupsQuery.data?.data?.map(group => group.id);
      setFilteredGroupIds(ids ?? []);
    }
  }, [agentGroupsQuery.isSuccess, agentGroupsQuery.data]);

  const filters = useFilterReducer({
    id: 'live-chat-tickets',
    initialFilters: { group_ids: [] }
  });

  const effectiveGroupIds =
    filters.filters.group_ids.length > 0
      ? filters.filters.group_ids
      : (
        filteredGroupIds.length > 0
          ? [...filteredGroupIds, 'null']
          : []
      );

  const isFiltered = filters.filters.group_ids.length > 0;

  const query = useQuery({
    queryKey: [
      'tickets/ubidesk/sidebar',
      {
        page: 1,
        per_page: 100,
        ticket_types: ['live-chat'],
        statuses: ['submitted', 'in_progress'],
        assignee_ids: [agent.id, 'null'],
        order: 'status',
        direction: 'desc',
        group_ids: effectiveGroupIds,
        is_filtered: isFiltered
      }
    ],
    refetchIntervalInBackground: REFRESH_INTERVAL,
    refetchInterval: REFRESH_INTERVAL
  });

  const initializeAudio = useCallback(() => {
    if (!audioInitialized) {
      progressingAudio.current?.load?.();
      submittedAudio.current?.load?.();
      setAudioInitialized(true);
    }
  }, [audioInitialized]);

  const playNewTicketAudio = useCallback(() => {
    if (!agent.sound_enabled || localStorage.getItem('liveChatSoundsMuted')) {
      return;
    }

    submittedAudio.current.currentTime = 0;
    submittedAudio.current.play();
  }, [agent]);

  const playProgressingTicketAudio = useCallback(() => {
    if (!agent.sound_enabled || localStorage.getItem('liveChatSoundsMuted')) {
      return;
    }

    progressingAudio.current.currentTime = 0;
    progressingAudio.current.play();
  }, [agent]);

  const isActiveTicket = useCallback(
    ticket => location.pathname === `/live-chat/${ticket.id}`,
    [location.pathname]
  );

  const handleMessage = useCallback(
    ({ responses }) => {
      if (!query.isSuccess || !query.data || !query.data.data) {
        return;
      }

      const ticket = query.data.data.find(
        ticket => ticket.subscriber.id === responses[0].subscriber.id
      );

      if (!ticket) {
        return;
      }

      if (!isActiveTicket(ticket)) {
        setUnreadTicketIds(unreadTicketIds => unreadTicketIds.concat(ticket.id));
      }

      updateTicketResponsesCache({ ticketId: ticket.id, responses });

      // Play appropriate sound based on ticket status
      if (ticket.status === 'in_progress') {
        playProgressingTicketAudio(); // Claimed ticket got a message
      } else if (ticket.status === 'submitted') {
        playNewTicketAudio(); // Unclaimed ticket got a message (new activity)
      }
    },
    [query, updateTicketResponsesCache, isActiveTicket, playProgressingTicketAudio, playNewTicketAudio]
  );

  const handleSubscriberLeavingChat = useCallback(
    ({ ticket }) => {
      updateTicketCache({
        ticketId: ticket.id,
        callback: oldTicket => ({ ...oldTicket, is_active: false })
      });

      if (!isActiveTicket(ticket)) {
        setUnreadTicketIds(unreadTicketIds => unreadTicketIds.concat(ticket.id));
      }
    },
    [updateTicketCache, isActiveTicket]
  );

  useSubscription('message', handleMessage);
  useNotifySubscription('SUBSCRIBER_LEFT_LIVE_CHAT', handleSubscriberLeavingChat);

  const handleNewTickets = useCallback(
    newTickets => {
      notify({
        title: 'Live chat',
        body: `${newTickets.length} new ticket${newTickets.length > 1 ? 's' : ''}`
      });
      setTitle(`Live chat: new ticket`);
      setUnreadTicketIds(newTickets.map(ticket => ticket.id));
      playNewTicketAudio();
    },
    [notify, setTitle, playNewTicketAudio]
  );

  useTicketMonitor(
    query.isSuccess && query.data && query.data.data ? query.data.data : null,
    handleNewTickets
  );

  const handleGroupsChange = groups => {
    filters.updateFilters({
      group_ids: groups ? groups.map(group => group.value) : []
    });
  };

  const handleLinkClick = ticket => {
    if (!audioInitialized) {
      initializeAudio();
    }

    setUnreadTicketIds(ids => ids.filter(id => id !== ticket.id));
  };

  return (
    <SecondarySidebarContainer style={{ boxShadow: 'none' }}>
      <AnimatePresence>
        {showSettings && (
          <PanelSlider
            header="Live chat settings"
            handleHide={() => {
              setShowSettings(false)
              queryClient.invalidateQueries({ queryKey: ['live-chat/suggestions'] });
            }}
            width={'30rem'}>
            <MemoizedLiveChatSettings />
          </PanelSlider>
        )}
      </AnimatePresence>
      <Flex col yScreen borderRight>
        <Flex fat mb>
          <Flex between fat>
            <HeadingContainer grow tall fat pad between center>
              <Heading1>Live chat</Heading1>
              <Flex col ySpace>
                <PermissionFilter can="view live chat settings">
                  <Button
                    variant="secondary"
                    icon="cog"
                    onClick={() => setShowSettings(true)}>
                    Settings
                  </Button>
                </PermissionFilter>
                <FilterMenu
                  buttonProps={{
                    fat: true,
                    loading: query.isLoading
                  }}
                  {...filters.props}>
                  <Flex col style={{ width: '20rem' }}>
                    <Label htmlFor="tags">Tags</Label>
                    <GroupSelect
                      id="tags"
                      for="tickets"
                      filteredGroupIds={filteredGroupIds}
                      value={filters.filters.group_ids}
                      onChange={handleGroupsChange}
                      placeholder="Filter with tags"
                    />
                  </Flex>
                </FilterMenu>
              </Flex>
            </HeadingContainer>
          </Flex>
        </Flex>
        {query.isLoading && (
          <Flex col yScreen pad borderRight>
            <Placeholder />
          </Flex>
        )}
        {query.isSuccess && query.data.data.length === 0 && (
          <Flex pl pr mt>
            <Span light>No available live chats</Span>
          </Flex>
        )}
        {query.isSuccess && query.data.data.length > 0 && (
          <Flex col yScroll tall>
            <AnimateSharedLayout>
              {query.data.data.map((ticket, key) => {
                const isUnread = unreadTicketIds.includes(ticket.id);
                return (
                  <React.Fragment key={ticket.id}>
                    {key === 0 && <Status>{statuses[ticket.status]}</Status>}
                    {key !== 0 &&
                      ticket.status !== query.data.data[key - 1].status && (
                        <Status>{statuses[ticket.status]}</Status>
                      )}
                    <SidebarLinkContainer
                      id="container"
                      layoutId={ticket.id}
                      layout="position"
                      unread={isUnread}
                      active={isActiveTicket(ticket)}>
                      <Link
                        id="link"
                        to={`/live-chat/${ticket.id}`}
                        aria-label="View ticket"
                        onClick={() => handleLinkClick(ticket)}>
                        <Flex col pad>
                          <Flex between fat center mbSm>
                            <Label style={isUnread && { fontWeight: 700 }} mb={false}>
                              User #{ticket.subscriber.id}
                            </Label>
                            <Flex center xSpaceSm>
                              {isUnread && <Indicator colour="primary" />}
                              <Label
                                style={isUnread && { fontWeight: 700 }}
                                colour={isUnread ? 'primary' : 'grey'}
                                mb={false}>
                                {dayjs(ticket.subscriber.latest_message.sent_at).from()}
                              </Label>
                            </Flex>
                          </Flex>
                          {ticket.groups.length > 0 && (
                            <Flex xSpaceSm mbSm>
                              {ticket.groups.map((group, key) => (
                                <Tag key={key}>{group.name}</Tag>
                              ))}
                            </Flex>
                          )}
                          <MessagePreview sm secondary={!isUnread}>
                            {ticket.subscriber.latest_message.content.text}
                          </MessagePreview>
                        </Flex>
                      </Link>
                    </SidebarLinkContainer>
                  </React.Fragment>
                );
              })}
            </AnimateSharedLayout>
          </Flex>
        )}
      </Flex>
    </SecondarySidebarContainer>
  );
};

Sidebar.propTypes = {
  agent: PropTypes.shape({
    id: PropTypes.number.isRequired,
    sound_enabled: PropTypes.bool.isRequired
  })
};

export default Sidebar;